<script setup>
import { Head, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import Scheduler from "../../../Components/Management/Scheduler.vue";
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import { useToast } from "primevue/usetoast";
import { ref, watch, computed } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { usePage } from "@inertiajs/inertia-vue3";

const props = defineProps([
    'companies',
    'selectedCompany',
    'filters',
    'flash'
]);

const route   = window.route;
const toast   = useToast();
const page    = usePage();
const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');

const form = useForm({
    company_id: props.selectedCompany?.uuid,
    working_hours: props.selectedCompany?.working_hours || [],
});

const selectedCompanyUuid = ref(props.filters?.company);

watch(() => props.filters?.company, (newVal) => {
    if (newVal !== selectedCompanyUuid.value) {
        selectedCompanyUuid.value = newVal;
    }
});

const handleCompanyChange = (e) => {
    selectedCompanyUuid.value = e.value;
    Inertia.get(route('management.companies.scheduler'), { company: e.value }, {
        preserveState: false,
        preserveScroll: true,
        only: ['selectedCompany', 'filters']
    });
};

watch(() => props.selectedCompany?.uuid, (newId) => {
    if (newId && newId !== form.company_id) {
        form.company_id = newId;
        form.working_hours = props.selectedCompany?.working_hours
            ? JSON.parse(JSON.stringify(props.selectedCompany.working_hours))
            : [];
    }
});

const submit = () => {
    if (!props.selectedCompany) return;
    
    // Client-side validation
    form.clearErrors();
    let hasClientErrors = false;
    
    form.working_hours.forEach((h, idx) => {
        if (h.is_workday) {
            if (!h.start_time) {
                form.setError(`working_hours.${idx}.start_time`, 'Beginn ist erforderlich');
                hasClientErrors = true;
            }
            if (!h.end_time) {
                form.setError(`working_hours.${idx}.end_time`, 'Ende ist erforderlich');
                hasClientErrors = true;
            }
            if (h.start_time && h.end_time && h.start_time >= h.end_time) {
                form.setError(`working_hours.${idx}.end_time`, 'Ende muss nach Beginn liegen');
                hasClientErrors = true;
            }
        }
    });

    if (hasClientErrors) {
        toast.add({
            severity: 'error',
            summary: 'Validierungsfehler',
            detail: 'Bitte prüfen Sie die markierten Zeiten.',
            life: 5000,
        });
        return;
    }
    
    form.put(route('management.companies.update-working-hours', props.selectedCompany.uuid), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Öffnungszeiten wurden erfolgreich gespeichert.',
                life: 5000,
            });
        },
        onError: (errors) => {
            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(field => {
                    const message = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
                    toast.add({
                        severity: 'error',
                        summary: 'Fehler',
                        detail: message,
                        life: 5000,
                    });
                });
            } else {
                toast.add({
                    severity: 'error',
                    summary: 'Fehler beim Speichern',
                    life: 5000,
                });
            }
        }
    });
};

</script>

<template>
    <Layout :flash="flash">
        <Head title="Öffnungszeiten Hub" />

        <div class="space-y-6">
            <ManagementHeader title="Unternehmen Management" module="companies" :activeIndex="1"
                              :tabOverrides="isSuperAdmin ? [] : ['Unternehmensdetails']" />

            <!-- Top Selector Section — super_admin only -->
            <div v-if="isSuperAdmin"
                 class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                        <i class="pi pi-building text-gray-400" />
                    </div>
                    <div>
                        <h3 class="font-bold text-gray-700">Unternehmen wählen</h3>
                        <p class="text-xs text-gray-400">Allgemeine Öffnungszeiten der Standorte verwalten</p>
                    </div>
                </div>
                
                <div class="w-full md:w-80">
                    <Dropdown 
                        v-model="selectedCompanyUuid" 
                        @change="handleCompanyChange"
                        :options="companies" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Standort suchen..." 
                        filter 
                        class="w-full rounded-xl border-gray-200"
                    >                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>

            <!-- Content Area -->
            <div v-if="selectedCompany" class="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div class="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <!-- Company Info Bar -->
                    <div class="px-8 py-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-4">
                            <div class="w-12 h-12 rounded-2xl bg-black flex items-center justify-center text-[#FFF053] font-bold text-xl">
                                {{ selectedCompany.name.charAt(0) }}
                            </div>
                            <div>
                                <h2 class="text-xl font-bold text-gray-800">{{ selectedCompany.name }}</h2>
                                <p class="text-sm text-gray-400">Haupt-Öffnungszeiten des Standorts</p>
                            </div>
                        </div>
                    </div>

                    <form :key="selectedCompany?.uuid" @submit.prevent="submit">
                        <Scheduler 
                            v-model="form.working_hours" 
                            title="Wöchentliche Öffnungszeiten" 
                            :errors="form.errors"
                            @clear-error="(key) => form.clearErrors(key)"
                        />
                        
                        <div class="px-8 py-6 bg-gray-50 border-t border-gray-100 flex justify-end">
                            <Button type="submit" 
                                    class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200" 
                                    :loading="form.processing" :disabled="form.processing">
                                <i class="pi pi-save" />
                                Öffnungszeiten speichern
                            </Button>
                        </div>
                    </form>
                </div>
            </div>

            <!-- Empty State — only shown to super_admin when no company selected -->
            <div v-else-if="isSuperAdmin" class="py-20 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
                <div class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                    <i class="pi pi-search text-2xl text-gray-300" />
                </div>
                <h3 class="text-lg font-bold text-gray-400">Kein Standort ausgewählt</h3>
                <p class="text-gray-300">Bitte wählen Sie oben einen Standort aus, um die Öffnungszeiten zu bearbeiten.</p>
            </div>
        </div>
    </Layout>
</template>
